// 偵測 section（spec 21 §3.1）— Motion/Object/Sound/SoundRecog/Tamper。
// Optional members (Sensitivity / ConfidenceThreshold / CurrentLevel) are probed
// via op:get; on failure their widgets hide themselves (handled in widgets/ops).
// Sub chips are advertise-only (no toggles; v1 has no per-event subscribe).

import { useTranslation } from 'react-i18next';
import {
  CL_MOTION,
  CL_OBJECT,
  CL_SOUND,
  CL_SOUNDRECOG,
  CL_TAMPER,
  MOTION_ENABLED,
  MOTION_ALARM_INTERVAL,
  MOTION_DETECTION_SCHEDULE,
  MOTION_SENSITIVITY,
  MOTION_ZONE_ENABLED,
  motionSubLabels,
  OBJECT_CONFIDENCE,
  OBJECT_ENABLED,
  objectSubLabels,
  SOUNDRECOG_ENABLED,
  soundRecogSubLabels,
  SOUND_CURRENT_LEVEL,
  SOUND_ENABLED,
  SOUND_LEVEL,
  TAMPER_ENABLED,
} from './ids';
import { useAttr } from './ops';
import { Chips, ReadonlyRow, SectionCard, SegmentedRow, SliderRow, ToggleRow } from './widgets';
import { ZoneEditor } from './ZoneEditor';
import { WeeklyScheduleEditor } from './WeeklyScheduleEditor';

export interface DetectionProps {
  deviceId: string;
  wsOpen: boolean;
  clusters: Set<string>;
  subs: Map<string, string[]>;
}

export function DetectionSection({ deviceId, wsOpen, clusters, subs }: DetectionProps) {
  const { t } = useTranslation();
  const hasMotion = clusters.has(CL_MOTION);
  const hasObject = clusters.has(CL_OBJECT);
  const hasSound = clusters.has(CL_SOUND);
  const hasRecog = clusters.has(CL_SOUNDRECOG);
  const hasTamper = clusters.has(CL_TAMPER);

  const motion = useAttr(deviceId, MOTION_ENABLED, wsOpen && hasMotion);
  const sensitivity = useAttr(deviceId, MOTION_SENSITIVITY, wsOpen && hasMotion); // optional → probe
  const zoneEnabled = useAttr(deviceId, MOTION_ZONE_ENABLED, wsOpen && hasMotion); // APP#45
  const alarmInterval = useAttr(deviceId, MOTION_ALARM_INTERVAL, wsOpen && hasMotion);
  const object = useAttr(deviceId, OBJECT_ENABLED, wsOpen && hasObject);
  const confidence = useAttr(deviceId, OBJECT_CONFIDENCE, wsOpen && hasObject); // optional → probe
  const sound = useAttr(deviceId, SOUND_ENABLED, wsOpen && hasSound);
  const soundLevel = useAttr(deviceId, SOUND_LEVEL, wsOpen && hasSound);
  const currentLevel = useAttr(deviceId, SOUND_CURRENT_LEVEL, wsOpen && hasSound); // optional → probe
  const recog = useAttr(deviceId, SOUNDRECOG_ENABLED, wsOpen && hasRecog);
  const tamper = useAttr(deviceId, TAMPER_ENABLED, wsOpen && hasTamper);

  if (!wsOpen) {
    return (
      <SectionCard title={t('deviceSettings.detection')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('deviceSettings.detectionConnecting')}
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t('deviceSettings.detection')}>
      {hasMotion && (
        <>
          <ToggleRow label={t('deviceSettings.motionDetection')} attr={motion} />
          {(
            <SegmentedRow
              label={t('deviceSettings.motionSensitivity')}
              attr={sensitivity}
              options={[
                [0, t('deviceSettings.sensitivityLow')],
                [1, t('deviceSettings.sensitivityMedium')],
                [2, t('deviceSettings.sensitivityHigh')],
              ]}
            />
          )}
          {/* APP#45 偵測區域限定 (ZoneEnabled + DetectionZones raw op:set) */}
          {(
            <>
              <ToggleRow
                label={t('deviceSettings.zoneEnabled')}
                hint={t('deviceSettings.zoneEnabledHint')}
                attr={zoneEnabled}
                disabled={zoneEnabled.status === 'unavailable'}
              />
              {(zoneEnabled.value === true || zoneEnabled.value === 1) && zoneEnabled.status !== 'unavailable' && (
                <ZoneEditor deviceId={deviceId} />
              )}
              <SegmentedRow
                label={t('deviceSettings.alarmInterval')}
                hint={t('deviceSettings.alarmIntervalHint')}
                attr={alarmInterval}
                options={[
                  [180, t('deviceSettings.alarmIntervalMinutes', { count: 3 })],
                  [300, t('deviceSettings.alarmIntervalMinutes', { count: 5 })],
                  [600, t('deviceSettings.alarmIntervalMinutes', { count: 10 })],
                  [1800, t('deviceSettings.alarmIntervalMinutes', { count: 30 })],
                ]}
              />
              <WeeklyScheduleEditor
                deviceId={deviceId}
                attrId={MOTION_DETECTION_SCHEDULE}
                kind="detection"
                wsOpen={wsOpen}
                disabled={false}
              />
            </>
          )}
          <Chips label={t('deviceSettings.supportedMotionEvents')} subs={subs.get(CL_MOTION) ?? []} labels={motionSubLabels()} />
        </>
      )}
      {hasObject && (
        <>
          <ToggleRow label={t('deviceSettings.objectDetection')} attr={object} />
          <SliderRow label={t('deviceSettings.objectConfidence')} attr={confidence} min={0} max={100} unit="%" />
          <Chips label={t('deviceSettings.supportedObjectTypes')} subs={subs.get(CL_OBJECT) ?? []} labels={objectSubLabels()} />
        </>
      )}
      {hasSound && (
        <>
          <ToggleRow label={t('deviceSettings.soundDetection')} attr={sound} />
          <SliderRow label={t('deviceSettings.soundLevelThreshold')} attr={soundLevel} min={0} max={120} unit=" dB" hint={t('deviceSettings.soundLevelHint')} />
          {currentLevel.status !== 'unavailable' && <ReadonlyRow label={t('deviceSettings.currentSoundLevel')} attr={currentLevel} render={(v) => `${String(v)} dB`} />}
        </>
      )}
      {hasRecog && (
        <>
          <ToggleRow label={t('deviceSettings.soundRecognition')} attr={recog} />
          <Chips label={t('deviceSettings.supportedSoundTypes')} subs={subs.get(CL_SOUNDRECOG) ?? []} labels={soundRecogSubLabels()} />
        </>
      )}
      {hasTamper && <ToggleRow label={t('deviceSettings.tamperDetection')} attr={tamper} />}
    </SectionCard>
  );
}
